import { REFINEMENT_PREFIX } from './character-xp.ts'
import { VOIE_SKILL_PREFIX } from './voie-granted-prayers.ts'

export { REFINEMENT_PREFIX, VOIE_SKILL_PREFIX }

export const MAITRISE_PREFIX = 'Maîtrise de la foi - '
export const SPEC_MYSTIQUE_PREFIX = 'Spécialisation mystique - '
export const DEVOT_SPEC_I_PREFIX = 'Spécialisation du Dévot I - '
export const DEVOT_SPEC_II_PREFIX = 'Spécialisation du Dévot II - '

export const WEAPON_TYPE_OPTIONS = [
	{ value: 'ARME_COURTE', label: 'Arme Courte' },
	{ value: 'ARME_LONGUE', label: 'Arme Longue' },
	{ value: 'ARME_DEUX_MAINS', label: 'Arme à deux mains' },
	{ value: 'ARME_HAST', label: "Arme d'Hast" },
]

export const ALCHEMY_SPECIALTY_OPTIONS = [
	{ value: 'ELIXIR', label: 'Élixirs' },
	{ value: 'ENCENS', label: 'Encens' },
	{ value: 'DROGUE', label: 'Drogues' },
	{ value: 'POISON', label: 'Poisons' },
]

/** Skills whose per-purchase choice is drawn from a fixed option list. */
export const SKILL_OPTION_SETS: Record<
	string,
	{ label: string; options: Array<{ value: string; label: string }> }
> = {
	'Spécialisation martiale': {
		label: "Type d'arme (un par achat)",
		options: WEAPON_TYPE_OPTIONS,
	},
	"Perfection de l'arme": {
		label: "Type d'arme (un par achat)",
		options: WEAPON_TYPE_OPTIONS,
	},
	Apothicaire: {
		label: 'Spécialité',
		options: ALCHEMY_SPECIALTY_OPTIONS,
	},
}

/** Keyed by the voie suffix (after `VOIE_SKILL_PREFIX`); values are Belief names. */
export const DEVOT_VOIE_BELIEF_BY_NAME: Record<string, string> = {
	'Voie de la Thorva': 'Stoïcisme de Werden',
	"Voie d'Urhm": 'Stoïcisme de Werden',
	'Voie du Pontif': 'Église Bradorienne',
	'Voie du Peuple': 'Église Bradorienne',
	"Voie d'Himme": 'Voies de Forsvar',
	'Voie de Cyre': 'Voies de Forsvar',
	"Voie d'Hérion": 'Voies de Forsvar',
	'Voie de Myhr': 'Dogmatisme de Vinerën',
	"Voie d'Akkar": 'Dogmatisme de Vinerën',
	"Voie d'Hevn": 'Dogmatisme de Vinerën',
	'Voie des Saisons': 'Cycle des Quatres',
	'Voie du Renouveau': 'Cycle des Quatres',
	'Voie de Vigmark': 'Cycle des Quatres',
}

/** Skills whose prerequisites are satisfied by any one parent, not all of them. */
export const ANY_OF_PREREQUISITE_SKILLS = new Set([
	'Artisanat',
	'Ritualisme Avancé',
])

export type SkillGroupRule = {
	prefix: string
	label: string
	rule: string
	byBelief?: boolean
}

export const SKILL_GROUP_RULES: SkillGroupRule[] = [
	{
		prefix: REFINEMENT_PREFIX,
		label: 'Raffinement',
		rule: '1 gratuit au départ, les autres achetables',
	},
	{
		prefix: MAITRISE_PREFIX,
		label: 'Maîtrise de la foi',
		rule: '1 au choix (2 avec Champion)',
	},
	{
		prefix: SPEC_MYSTIQUE_PREFIX,
		label: 'Spécialisation mystique',
		rule: '1 au choix',
	},
	{
		prefix: VOIE_SKILL_PREFIX,
		label: 'Perfectionnement de la voie',
		rule: '1 au choix, selon la croyance',
		byBelief: true,
	},
	{
		prefix: DEVOT_SPEC_I_PREFIX,
		label: 'Spécialisation du Dévot I',
		rule: '1 au choix',
	},
	{
		prefix: DEVOT_SPEC_II_PREFIX,
		label: 'Spécialisation du Dévot II',
		rule: '1 au choix, doit correspondre à la Spécialisation I',
	},
]

export function findSkillGroupRule(name: string) {
	return SKILL_GROUP_RULES.find((rule) => name.startsWith(rule.prefix)) ?? null
}

export function skillGroupMemberLabel(name: string) {
	const rule = findSkillGroupRule(name)
	return rule ? name.slice(rule.prefix.length).trim() : name
}

export function voieBeliefName(name: string) {
	if (!name.startsWith(VOIE_SKILL_PREFIX)) return null
	return DEVOT_VOIE_BELIEF_BY_NAME[skillGroupMemberLabel(name)] ?? null
}

type GroupableSkill = { name: string; tier: number | null }

export type CatalogueEntry<T extends GroupableSkill> =
	| { kind: 'skill'; skill: T; tier: number }
	| {
			kind: 'group'
			label: string
			rule: string
			tier: number
			members: T[]
	  }

/**
 * Collapses prefix-named siblings under one entry so a catalogue shows
 * "Maîtrise de la foi (1 au choix)" with six members instead of six rows.
 */
export function groupCatalogueSkills<T extends GroupableSkill>(
	skills: T[],
): Array<CatalogueEntry<T>> {
	const entries: Array<CatalogueEntry<T>> = []
	const groups = new Map<
		string,
		Extract<CatalogueEntry<T>, { kind: 'group' }>
	>()

	for (const skill of skills) {
		const rule = findSkillGroupRule(skill.name)
		if (!rule) {
			entries.push({ kind: 'skill', skill, tier: skill.tier ?? 0 })
			continue
		}
		let group = groups.get(rule.prefix)
		if (!group) {
			group = {
				kind: 'group',
				label: rule.label,
				rule: rule.rule,
				tier: skill.tier ?? 0,
				members: [],
			}
			groups.set(rule.prefix, group)
			entries.push(group)
		}
		group.tier = Math.min(group.tier, skill.tier ?? 0)
		group.members.push(skill)
	}

	for (const group of groups.values()) {
		group.members.sort((a, b) => a.name.localeCompare(b.name, 'fr'))
	}

	return entries.sort((a, b) => {
		if (a.tier !== b.tier) return a.tier - b.tier
		const an = a.kind === 'skill' ? a.skill.name : a.label
		const bn = b.kind === 'skill' ? b.skill.name : b.label
		return an.localeCompare(bn, 'fr')
	})
}
